import { Check } from "lucide-react";
import type { CategoryEntitlement } from "../../context/auth-me";
import { cn } from "../../lib/utils";
import { impliedPermissions, isCategoryEntitled } from "./access-model";
import { PERMISSION_CATEGORIES } from "./permissions";

const LABELS = new Map(PERMISSION_CATEGORIES.flatMap((c) => c.permissions.map((p) => [p.id, p.label] as const)));

/**
 * Inline grants on top of a role. Role-implied permissions show as fixed, and a category
 * the organization does not hold says so instead of offering a checkbox that would be a
 * no-op: "not in your plan" and "not granted to this member" are different answers.
 */
export function PermissionPicker({ role, value, onChange, entitlements, disabled }: {
  role: string;
  value: string[];
  onChange: (next: string[]) => void;
  entitlements: Record<string, CategoryEntitlement> | null | undefined;
  disabled?: boolean;
}) {
  const implied = new Set(impliedPermissions(role));
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((p) => p !== id) : [...value, id]);

  return (
    <div className="space-y-3">
      {implied.size > 0 && (
        <p className="text-xs text-ink-3">
          <span className="font-semibold text-ink-2">Includes: </span>
          {Array.from(implied).map((id) => LABELS.get(id) ?? id).join(", ")}
        </p>
      )}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {PERMISSION_CATEGORIES.map((category) => (
          <div key={category.id} className="space-y-2">
            <h5 className="text-xs font-semibold uppercase tracking-wider text-ink-3">{category.label}</h5>
            {!isCategoryEntitled(entitlements, category.id) ? (
              <p className="text-xs text-ink-3">Not enabled for your organization</p>
            ) : (
              <div className="space-y-1.5">
                {category.permissions.map((perm) => {
                  const fromRole = implied.has(perm.id);
                  const checked = fromRole || value.includes(perm.id);
                  const locked = fromRole || !!disabled;
                  return (
                    <label key={perm.id} className={cn("group flex items-start gap-2", locked ? "cursor-default" : "cursor-pointer")}>
                      <div className={cn(
                        "mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors",
                        checked ? "border-brand bg-brand" : "border-line bg-white group-hover:border-brand/50",
                        fromRole && "opacity-60",
                      )}>
                        {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                      </div>
                      <input
                        type="checkbox"
                        className="hidden"
                        aria-label={perm.label}
                        checked={checked}
                        disabled={locked}
                        onChange={() => toggle(perm.id)}
                      />
                      <span className="text-sm text-ink-2 group-hover:text-ink">
                        {perm.label}
                        {fromRole && <span className="text-ink-3"> · from role</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
